import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import {createPortal} from 'react-dom';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { Broadcast } from '@phosphor-icons/react';
import { listAdkSessions, listActiveAdkSessions, parseVfsSessionId } from '../services/AdkSessionsService';
import './BackgroundTasksIndicator.css';

const POLL_INTERVAL_MS = 4000;

/**
 * "Background tasks" — live visibility into agent sessions actively working
 * right now (a `/run_sse` in flight, per adk-be-app's `active_runs`
 * registry), the same concept as this coding session's own task
 * notifications: dispatch, watch it run, get notified, jump back in.
 *
 * Polls the lightweight `/active` endpoint for the set of active session
 * ids, and only fetches full session details (for name/labels) once there's
 * something to show — the common case (nothing running) stays a single
 * small request every POLL_INTERVAL_MS.
 */
const BackgroundTasksIndicator = ({ userId, tenantId, collapsed = false }) => {
  const [activeIds, setActiveIds] = useState([]);
  const [sessionsById, setSessionsById] = useState({});
  const [isOpen, setIsOpen] = useState(false);
  const [panelPos, setPanelPos] = useState(null);
  const navigate = useNavigate();
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  const poll = useCallback(async () => {
    if (!userId) { return; }
    try {
      const ids = await listActiveAdkSessions(userId);
      setActiveIds((prev) => {
        // Only churn state when the set actually changed, so open-panel
        // polling doesn't re-render/flicker for no reason.
        if (prev.length === ids.length && prev.every((id) => ids.includes(id))) {
          return prev;
        }
        return ids;
      });
    } catch {
      // Best-effort UI affordance — a transient failure just means the
      // indicator doesn't update this tick, not worth surfacing to the user.
    }
  }, [userId]);

  useEffect(() => {
    poll();
    const interval = setInterval(poll, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [poll]);

  // Once there's at least one active id, fetch real session details (name,
  // last-update) so the panel can show something more useful than a bare id.
  useEffect(() => {
    if (activeIds.length === 0 || !userId) { return; }
    let cancelled = false;
    listAdkSessions(userId).then((sessions) => {
      if (cancelled) { return; }
      const byId = {};
      for (const s of sessions) { byId[s.id] = s; }
      setSessionsById(byId);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [activeIds, userId]);

  // Real live bug: this panel used to be `position: absolute` inside the
  // trigger's own (small, bottom-of-sidebar) container, with a fixed
  // `top: calc(100% + 8px)` — right next to the viewport's bottom edge,
  // that pushed most of the panel off-screen / overlapping whatever sits
  // above it instead of appearing as a clean floating panel. Same root
  // cause and same fix as UserAccountMenu.jsx's popover: portal to
  // document.body, position: fixed from the trigger's real
  // getBoundingClientRect, flip up/down based on actual available space.
  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const PANEL_HEIGHT_ESTIMATE = 240;
    const opensUp = rect.top > PANEL_HEIGHT_ESTIMATE + 16;
    setPanelPos({
      left: Math.max(8, rect.left),
      ...(opensUp
        ? {bottom: window.innerHeight - rect.top + 8}
        : {top: rect.bottom + 8}),
    });
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen, updatePosition]);

  useLayoutEffect(() => {
    if (isOpen) updatePosition();
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (!isOpen) { return undefined; }
    const handleClickOutside = (e) => {
      if (
        panelRef.current && !panelRef.current.contains(e.target) &&
        triggerRef.current && !triggerRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleSelect = (sessionId) => {
    setIsOpen(false);
    const parsed = parseVfsSessionId(sessionId);
    if (parsed?.projectId && tenantId) {
      navigate(`/code/${tenantId}/${parsed.projectId}?conversationId=${encodeURIComponent(sessionId)}`);
    } else {
      navigate(`/chat?conversationId=${encodeURIComponent(sessionId)}`);
    }
  };

  if (activeIds.length === 0) {
    // No badge/indicator at all when nothing is running — matches the
    // "quiet when idle" behavior of this session's own task notifications.
    return null;
  }

  return (
    <div className={`background-tasks-indicator ${collapsed ? 'collapsed' : ''}`} ref={triggerRef}>
      <button
        type="button"
        className="background-tasks-trigger"
        onClick={() => setIsOpen((v) => !v)}
        title={`${activeIds.length} agent task${activeIds.length === 1 ? '' : 's'} running`}
      >
        <Broadcast size={18} weight="fill" className="background-tasks-pulse" />
        {!collapsed && <span className="background-tasks-count">{activeIds.length}</span>}
      </button>
      {isOpen && panelPos && createPortal(
        <div className="background-tasks-panel background-tasks-panel-left" ref={panelRef} style={panelPos}>
          <div className="background-tasks-panel-header">Background tasks</div>
          <ul className="background-tasks-list">
            {activeIds.map((id) => {
              const session = sessionsById[id];
              const label = session?.name || id;
              return (
                <li key={id} className="background-tasks-item" onClick={() => handleSelect(id)}>
                  <span className="background-tasks-dot" />
                  <span className="background-tasks-label">{label}</span>
                </li>
              );
            })}
          </ul>
        </div>,
        document.body
      )}
    </div>
  );
};

BackgroundTasksIndicator.propTypes = {
  userId: PropTypes.string,
  tenantId: PropTypes.string,
  collapsed: PropTypes.bool,
};

export default BackgroundTasksIndicator;
